// Real US Bureau of Labor Statistics 10-year occupational projections
// (employment 2023–2033). Used to attach a `growth` field to every career
// the AI surfaces — we don't trust the model to invent these numbers.
//
// Source: https://www.bls.gov/ooh/ (Occupational Outlook Handbook).
// Last updated by hand: 2026-05-10.
//
// Lookup is case-insensitive, normalizes punctuation, and falls back to a
// neutral "Steady growth · +5%" if the title isn't in the table.

import type { ReportCareerGrowth } from '../dashboard/reportTypes';

interface GrowthRow {
  rate: number;        // signed integer, e.g. 23 for +23%, -2 for -2%
}

// Bucket label from the rate. Mirrors how BLS describes outlook in the OOH.
function labelFor(rate: number): { label: string; trend: 'up' | 'down' | 'flat' } {
  if (rate <= -1) return { label: 'Declining', trend: 'down' };
  if (rate <= 4) return { label: 'Avg. growth', trend: 'flat' };
  if (rate <= 8) return { label: 'Steady growth', trend: 'up' };
  if (rate <= 14) return { label: 'Faster than avg.', trend: 'up' };
  return { label: 'Much faster', trend: 'up' };
}

function fmt(rate: number): ReportCareerGrowth {
  const { label, trend } = labelFor(rate);
  const rateStr = (rate >= 0 ? '+' : '') + rate + '%';
  return { rate: rateStr, label, trend };
}

// Title → projected growth %. Keys are normalized lowercase strings (no
// punctuation, ampersand → and, parens removed). The ID slug is also
// supported as a fallback so AI-emitted "id: 'startup-coo'" matches.
const RAW: Record<string, GrowthRow> = {
  // — Tech & Product ——————————————————————————————————————
  'product manager': { rate: 19 },
  'senior product manager': { rate: 19 },
  'group product manager': { rate: 19 },
  'product owner': { rate: 19 },
  'software engineer': { rate: 17 },
  'software developer': { rate: 17 },
  'frontend engineer': { rate: 17 },
  'backend engineer': { rate: 17 },
  'full stack engineer': { rate: 17 },
  'mobile engineer': { rate: 17 },
  'engineering manager': { rate: 17 },
  'data engineer': { rate: 36 },
  'data scientist': { rate: 36 },
  'data analyst': { rate: 23 },
  'machine learning engineer': { rate: 36 },
  'ai engineer': { rate: 36 },
  'devops engineer': { rate: 19 },
  'site reliability engineer': { rate: 19 },
  'cloud engineer': { rate: 19 },
  'security engineer': { rate: 33 },
  'cybersecurity analyst': { rate: 33 },
  'information security analyst': { rate: 33 },
  'qa engineer': { rate: 17 },
  'technical program manager': { rate: 19 },
  'solutions architect': { rate: 17 },
  'database administrator': { rate: 9 },
  'systems administrator': { rate: 2 },
  'it support specialist': { rate: 5 },
  'network engineer': { rate: 4 },

  // — Design & UX ————————————————————————————————————————
  'ux designer': { rate: 13 },
  'ui designer': { rate: 13 },
  'product designer': { rate: 13 },
  'visual designer': { rate: 6 },
  'graphic designer': { rate: 2 },
  'web designer': { rate: 16 },
  'interaction designer': { rate: 13 },
  'design lead': { rate: 13 },
  'creative director': { rate: 6 },
  'art director': { rate: 6 },
  'user researcher': { rate: 12 },

  // — Strategy / Consulting / Ops ———————————————————————
  'management consultant': { rate: 11 },
  'strategy consultant': { rate: 11 },
  'business analyst': { rate: 11 },
  'operations analyst': { rate: 23 },
  'operations manager': { rate: 6 },
  'operations director': { rate: 5 },
  'chief of staff': { rate: 8 },
  'startup coo': { rate: 6 },
  'chief operating officer': { rate: 6 },
  'project manager': { rate: 6 },
  'program manager': { rate: 6 },
  'innovation manager': { rate: 7 },
  'strategic innovation manager': { rate: 7 },
  'chief innovation officer': { rate: 6 },

  // — Marketing & Brand ————————————————————————————————
  'marketing manager': { rate: 8 },
  'marketing director': { rate: 8 },
  'brand manager': { rate: 8 },
  'brand strategist': { rate: 9 },
  'content strategist': { rate: 10 },
  'content marketer': { rate: 10 },
  'growth marketer': { rate: 13 },
  'performance marketing manager': { rate: 13 },
  'seo specialist': { rate: 13 },
  'digital marketer': { rate: 13 },
  'product marketing manager': { rate: 8 },
  'social media manager': { rate: 11 },
  'communications lead': { rate: 8 },
  'communications manager': { rate: 8 },
  'public relations specialist': { rate: 6 },

  // — Sales & CS ———————————————————————————————————————
  'account executive': { rate: 4 },
  'sales manager': { rate: 4 },
  'sales director': { rate: 4 },
  'business development manager': { rate: 4 },
  'customer success manager': { rate: 6 },
  'customer support manager': { rate: 5 },
  'sales operations analyst': { rate: 8 },
  'partnerships manager': { rate: 6 },

  // — Finance & Analytics —————————————————————————————
  'financial analyst': { rate: 9 },
  'investment analyst': { rate: 9 },
  'investment banker': { rate: 4 },
  'corporate finance manager': { rate: 7 },
  'accountant': { rate: 4 },
  'controller': { rate: 4 },
  'fp&a analyst': { rate: 9 },
  'private equity associate': { rate: 4 },
  'venture capital associate': { rate: 4 },
  'actuary': { rate: 21 },
  'risk analyst': { rate: 9 },

  // — People & Org ———————————————————————————————————
  'recruiter': { rate: 8 },
  'talent acquisition specialist': { rate: 8 },
  'hr business partner': { rate: 6 },
  'hr manager': { rate: 6 },
  'people operations manager': { rate: 6 },
  'organizational development consultant': { rate: 7 },
  'compensation analyst': { rate: 4 },
  'learning and development manager': { rate: 7 },

  // — Healthcare / Life Sciences ——————————————————————
  'registered nurse': { rate: 6 },
  'nurse practitioner': { rate: 38 },
  'physical therapist': { rate: 14 },
  'occupational therapist': { rate: 11 },
  'physician assistant': { rate: 28 },
  'physician': { rate: 4 },
  'pharmacist': { rate: 5 },
  'medical and health services manager': { rate: 28 },
  'biostatistician': { rate: 23 },
  'epidemiologist': { rate: 19 },
  'mental health counselor': { rate: 18 },
  'social worker': { rate: 9 },

  // — Education ————————————————————————————————————
  'high school teacher': { rate: 1 },
  'elementary school teacher': { rate: 1 },
  'university professor': { rate: 8 },
  'instructional designer': { rate: 4 },
  'e-learning specialist': { rate: 4 },
  'curriculum designer': { rate: 4 },
  'school counselor': { rate: 4 },

  // — Legal ————————————————————————————————————————
  'lawyer': { rate: 5 },
  'attorney': { rate: 5 },
  'paralegal': { rate: 1 },
  'compliance officer': { rate: 6 },
  'general counsel': { rate: 5 },

  // — Creative & Media ———————————————————————————————
  'writer': { rate: 4 },
  'editor': { rate: 4 },
  'journalist': { rate: -3 },
  'producer': { rate: 7 },
  'video editor': { rate: 7 },
  'animator': { rate: 8 },
  'photographer': { rate: 4 },
  'illustrator': { rate: 2 },

  // — Trades & Built ———————————————————————————————
  'architect': { rate: 5 },
  'civil engineer': { rate: 6 },
  'mechanical engineer': { rate: 11 },
  'electrical engineer': { rate: 9 },
  'industrial engineer': { rate: 12 },
  'environmental engineer': { rate: 6 },
  'project engineer': { rate: 6 },
  'construction project manager': { rate: 9 },
  'urban planner': { rate: 4 },
  'interior designer': { rate: 4 },

  // — Mission-driven ———————————————————————————————
  'nonprofit program manager': { rate: 11 },
  'social impact pm': { rate: 11 },
  'grants manager': { rate: 8 },
  'community organizer': { rate: 9 },
  'policy analyst': { rate: 6 },

  // — Skilled trades ———————————————————————————————
  'electrician': { rate: 11 },
  'plumber': { rate: 6 },
  'carpenter': { rate: 4 },
  'hvac technician': { rate: 9 },
};

/** Normalize a job title for lookup. Lowercase, drop punctuation, trim. */
function normalize(title: string): string {
  return title
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Best-effort BLS growth lookup. Returns a neutral fallback when the title
 * isn't in our table — never throws, never returns null.
 */
export function getCareerGrowth(title: string, idSlug?: string): ReportCareerGrowth {
  const direct = RAW[normalize(title)];
  if (direct) return fmt(direct.rate);

  if (idSlug) {
    const slugKey = idSlug.replace(/-/g, ' ');
    const viaSlug = RAW[normalize(slugKey)];
    if (viaSlug) return fmt(viaSlug.rate);
  }

  // Fuzzy: try last 1–3 tokens of the title (handles "Senior Product Manager"
  // → "product manager"). Cheaper than full Levenshtein, good enough for
  // the title diversity we see.
  const tokens = normalize(title).split(' ');
  for (let n = Math.min(3, tokens.length); n >= 2; n--) {
    const tail = tokens.slice(-n).join(' ');
    const hit = RAW[tail];
    if (hit) return fmt(hit.rate);
  }

  // Neutral fallback — read as "we don't have a precise BLS number, but
  // industry trend is roughly steady".
  return fmt(5);
}
